import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../config';
import { useNavigate } from 'react-router-dom';
import './AdminDashboardPage.css';

// --- media URL helper (normalizes any legacy path to CloudFront) ---
const CDN_BASE = (process.env.REACT_APP_CDN_BASE || '').replace(/\/+$/, '');

function asMediaUrl(val) {
  if (!val) return '';
  let s = String(val).trim();

  // already absolute → pass through
  if (/^https?:\/\//i.test(s)) return s;

  // strip leading slashes
  s = s.replace(/^\/+/, '');

  // Normalize shapes:
  if (s.startsWith('uploads/roam_share_posts/')) {
    // correct, do nothing
  } else if (s.startsWith('uploads/')) {
    s = s.replace(/^uploads\/(?!roam_share_posts\/)/, 'uploads/roam_share_posts/');
  } else {
    s = `uploads/roam_share_posts/${s}`;
  }

  return CDN_BASE ? `${CDN_BASE}/${s}` : `/${s}`;
}


const AdminDashboardPage = () => {
  const { accessToken, user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsersToday: 0,
    activeUsersThisWeek: 0,
    activeUsersThisMonth: 0, 
    reportedPostsCount: 0,
    bannedUsersCount: 0,
  });
  
  const [accessDenied, setAccessDenied] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchUsername, setSearchUsername] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [warningNote, setWarningNote] = useState('');
  const [warnedUsers, setWarnedUsers] = useState({}); // userId: true
  const [allPosts, setAllPosts] = useState([]);
  const [reportedComments, setReportedComments] = useState([]);
  const [reportedUsers, setReportedUsers] = useState([]);
  const [postFilter, setPostFilter] = useState('all'); // 'all' | 'reported'
  const [previewMedia, setPreviewMedia] = useState(null); // { type, src }
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [activeUsersOnDate, setActiveUsersOnDate] = useState(null);
  const [activeUsersInMonth, setActiveUsersInMonth] = useState(null);


useEffect(() => {
  const fetchUsers = async () => {
    try {
      const res = await fetch(api.url('/api/admin/users'), {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setAllUsers(data || []);
        setFilteredUsers(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  };

  if (activeSection === 'settings') {
    fetchUsers();
  }
}, [activeSection, accessToken]);
 

  const handleUserSearch = () => {
    const query = searchUsername.toLowerCase();
  
    const results = allUsers.filter((u) => {
      if (searchStatus === 'username') {
        return u.username.toLowerCase().includes(query);
      } else if (searchStatus === 'email') {
        return u.email.toLowerCase().includes(query);
      } else if (searchStatus === 'status') {
        const isBanned = u.is_blocked;
        return (
          (query === 'active' && !isBanned) ||
          (query === 'banned' && isBanned)
        );
      }
      return true;
    });
  
    setFilteredUsers(results);
  };
  
  const handleToggleBan = async (userId, isBlocked) => {
    const action = isBlocked ? 'unblock' : 'block';
    const confirmMsg = `Are you sure you want to ${action} this user?`;
  
    if (!window.confirm(confirmMsg)) return;
  
    try {
      const res = await fetch(api.url(`/api/admin/${action}/${userId}`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
  
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Request failed: ${errorText}`);
      }
  
      const data = await res.json();
      console.log(`${action} success`, data);
      fetchUsers(); // refresh user list
    } catch (error) {
      console.error('Toggle Ban Error:', error.message);
      alert(`Error: ${error.message}`);
    }
  };
  
  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user and all related data?')) {
      try {
        const response = await fetch(api.url(`/api/admin/delete/${userId}`), {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
  
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText);
        }
  
        alert('User and related data deleted successfully');
        fetchUsers();
      } catch (error) {
        console.error('Delete User Error:', error);
        alert(`Error deleting user: ${error.message}`);
      }
    }
  };
  
  
  const openWarningModal = async (userId) => {
    setSelectedUserId(userId);
    setWarningNote('');
    setShowWarningModal(true);
  
    try {
      const res = await fetch(api.url(`/api/admin/users/${userId}/warnings/latest`), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
  
      if (res.ok) {
        const data = await res.json();
        if (data.reason) {
          setWarningNote(data.reason);
          setWarnedUsers((prev) => ({ ...prev, [userId]: true }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch existing warning:', err);
    }
  };
  
  
  const sendWarning = async () => {
    if (!warningNote.trim()) return alert('Warning message required.');
  
    try {
      const res = await fetch(api.url(`/api/admin/users/${selectedUserId}/warnings`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason: warningNote }),
      });
  
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to send warning');
      }
  
      alert('Warning saved');
      setWarnedUsers((prev) => ({ ...prev, [selectedUserId]: true }));
      setShowWarningModal(false);
    } catch (err) {
      console.error('Warning error:', err);
      alert(`Error: ${err.message}`);
    }
  };  
  
  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to permanently delete this post?')) return;
    try {
      const res = await fetch(api.url(`/api/admin/posts/${postId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      
      if (res.ok) {
        fetchPosts();
      } else {
        const errText = await res.text();
        throw new Error(errText);
      }
    } catch (err) {
      alert(`Failed to delete post: ${err.message}`);
    }
  };
const deleteReportedComment = async (commentId) => {
  if (!window.confirm('Are you sure you want to permanently delete this comment?')) return;

  try {
    const res = await fetch(api.url(`/api/admin/reported-comments/${commentId}`), {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (res.ok) {
      fetchReportedComments();
    } else {
      const errText = await res.text();
      throw new Error(errText);
    }
  } catch (err) {
    alert(`Failed to delete comment: ${err.message}`);
  }
};
  const clearReportStatus = async (postId) => {
  if (!window.confirm("Are you sure you want to mark this post as NOT reported?")) return;

  try {
    const res = await fetch(api.url(`/api/admin/posts/${postId}/clear-report`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      

    if (res.ok) {
      // Update post list to reflect change
      setAllPosts((prevPosts) =>
        prevPosts.map((p) =>
          p.post_id === postId ? { ...p, is_reported: false } : p
        )
      );
    } else {
      console.error("Failed to clear report");
    }
  } catch (err) {
    console.error("Clear report error:", err);
  }
};

  // Initial fetch for users and blocked logs
  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(api.url('/api/admin/users'), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
  
      if (res.ok) {
        const data = await res.json();
        setAllUsers(data || []);
        setFilteredUsers(data || []);
      } else {
        console.error('Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, [accessToken]);
  const fetchPosts = useCallback(async () => {
    try {
      const res = await fetch(api.url('/api/admin/posts'), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
  
      if (res.ok) {
        const data = await res.json();
        setAllPosts(data);
      } else {
        console.error('Failed to fetch posts');
      }
    } catch (err) {
      console.error('Error fetching posts:', err);
    }
  }, [accessToken]);
const fetchReportedComments = useCallback(async () => {
  try {
    const res = await fetch(api.url('/api/admin/reported-comments'), {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (res.ok) {
      const data = await res.json();
      setReportedComments(data || []);
    } else {
      console.error('Failed to fetch reported comments');
    }
  } catch (err) {
    console.error('Error fetching reported comments:', err);
  }
}, [accessToken]);
const fetchReportedUsers = useCallback(async () => {
  try {
    const res = await fetch(api.url('/api/admin/reported-users'), {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (res.ok) {
      const data = await res.json();
      setReportedUsers(data || []);
    } else {
      const errorText = await res.text();
      console.error('Failed to fetch reported users:', errorText);
    }
  } catch (err) {
    console.error('Error fetching reported users:', err);
  }
}, [accessToken]);
useEffect(() => {
  if (activeSection === 'posts') {
    fetchPosts();
  }

  if (activeSection === 'comments') {
    fetchReportedComments();
  }

  if (activeSection === 'reported-users') {
    fetchReportedUsers();
  }
}, [
  activeSection,
  fetchPosts,
  fetchReportedComments,
  fetchReportedUsers,
]);
  useEffect(() => {
    if (activeSection === 'users') {
      fetchUsers();
    }
  }, [activeSection, fetchUsers]);  
  
  useEffect(() => {
    console.log('Logged in user:', user);
    if (!accessToken || !user) {
      navigate('/');
      return;
    }

    if (user.role !== 'admin' && user.role !== 'super-admin') {
      setAccessDenied(true);
      return;
    }

    const fetchStats = async () => {
      try {
        const res = await fetch(api.url('/api/admin/dashboard'), {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (res.ok) {
          const data = await res.json();
          setStats(data);
        } else {
          console.error('Failed to fetch admin stats');
        }
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
      }
    };

    fetchStats();
  }, [accessToken, user, navigate]);
  const fetchActiveUsersByDate = useCallback(async () => {
    if (!selectedDate) return;
    try {
      console.log("📅 Fetching active users on date:", selectedDate);
      const res = await fetch( api.url(`/api/admin/dashboard/active-users?date=${encodeURIComponent(selectedDate)}`),
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );      
      const data = await res.json();
      console.log("📅 Response from backend for selected date:", data);
      setActiveUsersOnDate(data.activeUsers);
    } catch (err) {
      console.error("Error fetching active users by date:", err);
    }
  }, [selectedDate, accessToken]);
  
  const fetchActiveUsersByMonth = useCallback(async () => {
    if (!selectedMonth) return;
    try {
      console.log("🗓 Fetching active users in month:", selectedMonth);
      const res = await fetch( api.url(`/api/admin/dashboard/active-users?month=${encodeURIComponent(selectedMonth)}`),
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );      
      const data = await res.json();
      console.log("🗓 Response from backend for selected month:", data);
      setActiveUsersInMonth(data.activeUsers);
    } catch (err) {
      console.error("Error fetching active users by month:", err);
    }
  }, [selectedMonth, accessToken]);
  
  useEffect(() => {
    fetchActiveUsersByDate();
  }, [fetchActiveUsersByDate]);
  
  useEffect(() => {
    fetchActiveUsersByMonth();
  }, [fetchActiveUsersByMonth]);
  
  
  if (accessDenied) {
    return (
      <div className="admin-access-denied">
        <h2>Access Denied</h2>
        <p>You do not have access to this page.</p>
      </div>
    );
  }
  return (
    <div className="admin-dashboard-wrapper"> {/* Fix wrapper name */}
      <div className="admin-sidebar">
        <h2>Admin Panel</h2>
        <ul>
          <li className={activeSection === 'dashboard' ? 'active' : ''} onClick={() => setActiveSection('dashboard')}>Dashboard</li>
          <li className={activeSection === 'users' ? 'active' : ''} onClick={() => setActiveSection('users')}>Users</li>
          <li className={activeSection === 'posts' ? 'active' : ''} onClick={() => setActiveSection('posts')}>Posts</li>
         <li className={activeSection === 'comments' ? 'active' : ''} onClick={() => setActiveSection('comments')}>Comments</li>
         <li
  className={activeSection === 'reported-users' ? 'active' : ''}
  onClick={() => setActiveSection('reported-users')}
>
  Reported Users
</li>
         
        </ul>
      </div>
  
      <div className="admin-dashboard-container">
  {activeSection === 'dashboard' && (
    <>
      <div className="admin-dashboard-header-container">
        <div className="admin-dashboard-heading-row">
          <h2 className="admin-dashboard-header">Dashboard Overview</h2>
          <span className="admin-dashboard-username">Welcome, {user?.name || user?.username || 'Admin'}</span>
        </div>
        <hr className="admin-dashboard-divider" />
      </div>

      <div className="admin-cards-container">
        <div className="admin-card">
          <h3>Total Users</h3>
          <p>{stats?.totalUsers ?? 0}</p>
        </div>
        <div className="admin-card">
          <h3>Active Today</h3>
          <p>{stats?.activeUsersToday ?? 0}</p>
        </div>
        <div className="admin-card">
          <h3>Active This Week</h3>
          <p>{stats?.activeUsersThisWeek ?? 0}</p>
        </div>
        <div className="admin-card">
          <h3>Active This Month</h3>
          <p>{stats?.activeUsersThisMonth ?? 0}</p>
        </div>
        <div className="admin-card">
          <h3>Total Posts</h3>
          <p>{stats?.totalPosts ?? 0}</p>
        </div>
        <div className="admin-card">
          <h3>Reported Posts</h3>
          <p>{stats?.reportedPostsCount ?? 0}</p>
        </div>
        <div className="admin-card">
          <h3>Banned Users</h3>
          <p>{stats?.bannedUsersCount ?? 0}</p>
        </div>
        {/* Additional Cards for Custom Date/Month Filters */}
        <div className="admin-card">
  <h3>📅 Active on Selected Date</h3>
  <input
    type="date"
    value={selectedDate}
    onChange={(e) => {
      setSelectedDate(e.target.value);
      setActiveUsersOnDate(null); // reset to trigger loading indicator or "No data"
    }}
    className="admin-date-input"
  />
  {selectedDate && (
  <div>
    {activeUsersOnDate !== null ? (
      <h4>Users – <strong>{activeUsersOnDate}</strong></h4>
    ) : (
      <p style={{ fontSize: '13px', color: '#666' }}>Loading...</p>
    )}
  </div>
)}
</div>

<div className="admin-card">
  <h3>🗓️ Active in Selected Month</h3>
  <input
    type="month"
    value={selectedMonth}
    onChange={(e) => {
      setSelectedMonth(e.target.value);
      setActiveUsersInMonth(null); // reset to trigger loading indicator or "No data"
    }}
    className="admin-date-input"
  />
  {selectedMonth && (
  <div>
    {activeUsersInMonth !== null ? (
      <h4>Users – <strong>{activeUsersInMonth}</strong></h4>
    ) : (
      <p style={{ fontSize: '13px', color: '#666' }}>Loading...</p>
    )}
  </div>
)}
</div>
      </div>
    </>
  )}

{activeSection === 'users' && (
  <div className="admin-section">
    <h2>Manage Users</h2>
    <p>Total - {allUsers.length}</p>

    {/* Search Filters */}
    <div className="admin-user-filters">
  <select
    value={searchStatus}
    onChange={(e) => setSearchStatus(e.target.value)}
  >
    <option value="">Search by</option>
    <option value="username">Username</option>
    <option value="email">Email</option>
    <option value="status">Status</option>
  </select>

  <input
    type="text"
    placeholder={
      searchStatus === 'username'
        ? 'Enter username'
        : searchStatus === 'email'
        ? 'Enter email'
        : 'active / banned'
    }
    value={searchUsername}
    onChange={(e) => setSearchUsername(e.target.value)}
  />
  <button onClick={handleUserSearch}>Search</button>
</div>


    {/* Users Table */}
    <div className="admin-user-table-scroll">
      <table>
        <thead>
          <tr>
            <th>Username</th>
            <th>Email</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map((u) => (
            <tr key={u.id}>
              <td>{u.username}</td>
              <td>{u.email}</td>
              <td>{u.is_blocked ? 'banned' : 'active'}</td>
              <td>
              <button
               className={`btn ${u.is_blocked ? 'unblock-btn' : 'block-btn'}`}
               onClick={() => handleToggleBan(u.id, u.is_blocked)}
              >
               {u.is_blocked ? 'Unblock' : 'Block'}
              </button>
              <button className="btn delete-btn" onClick={() => handleDeleteUser(u.id)}>Delete</button>
              <button className={`btn warn-btn ${warnedUsers[u.id] ? 'warned' : ''}`} onClick={() => openWarningModal(u.id)}> Notes </button>
            </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Warning Modal */}
    {showWarningModal && (
      <div className="admin-modal-overlay">
        <div className="admin-modal">
          <h3>Issue Warning to User</h3>
          <textarea
            placeholder="Enter warning or flag note"
            value={warningNote}
            onChange={(e) => setWarningNote(e.target.value)}
          />
          <div className="admin-modal-actions">
            <button onClick={sendWarning}>Send Warning</button>
            <button onClick={() => setShowWarningModal(false)}>Cancel</button>
          </div>
        </div>
      </div>
    )}

    
  </div>
)}

{activeSection === 'posts' && (
  <div className="admin-section">
    <h2>All Posts</h2>

    {/* Filter Dropdown */}
    <div className="admin-post-filters">
      <select value={postFilter} onChange={(e) => setPostFilter(e.target.value)}>
        <option value="all">All Posts</option>
        <option value="reported">Reported Posts</option>
      </select>
    </div>

    {/* Posts Table */}
    <div className="admin-user-table-scroll">
      <table>
        <thead>
          <tr>
            <th>Preview</th>
            <th>Content</th>
            <th>Email</th>
            <th>Location</th>
            <th>Tags</th>
            <th>Type</th>
            <th>Date</th>
            <th>Likes</th>
            <th>Reported</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {allPosts
            .filter(p => postFilter === 'all' || p.is_reported)
            .map((p) => (
              <tr key={p.post_id}>
              <td>
  {p.post_type === "photo" && p.images?.length > 0 ? (
    <img
      src={asMediaUrl(p.images[0])}
      alt="preview"
      style={{ width: '60px', borderRadius: '4px', cursor: 'pointer' }}
      onClick={() => {
        setPreviewMedia({ type: 'photo', src: asMediaUrl(p.images[0]) });
        setShowModal(true);
      }}
      onError={(e) => {
        // replace the broken thumbnail with a clickable link to the exact URL we tried
        const attempted = asMediaUrl(p.images[0]);
        const a = document.createElement('a');
        a.href = attempted;
        a.textContent = attempted;
        a.target = '_blank';
        a.rel = 'noreferrer';
        e.currentTarget.replaceWith(a);
      }}
      title={asMediaUrl(p.images[0])}
    />
  ) : p.post_type === "video" && p.video ? (
    <video
      width="100"
      height="60"
      style={{ borderRadius: '4px', cursor: 'pointer' }}
      muted
      onClick={() => {
        setPreviewMedia({ type: 'video', src: asMediaUrl(p.video) });
        setShowModal(true);
      }}
      onError={(e) => {
        const attempted = asMediaUrl(p.video);
        const a = document.createElement('a');
        a.href = attempted;
        a.textContent = attempted;
        a.target = '_blank';
        a.rel = 'noreferrer';
        e.currentTarget.replaceWith(a);
      }}
      title={asMediaUrl(p.video)}
    >
      <source src={asMediaUrl(p.video)} type="video/mp4" />
    </video>
  ) : (
    <span>No Preview</span>
  )}
</td>


 <td>{p.content}</td>
 <td>{p.email || 'N/A'}</td>
 <td>{p.location}</td>
 <td>{p.tags?.join(', ')}</td>
 <td>{p.post_type}</td>
 <td>{new Date(p.created_at).toLocaleDateString()}</td>
 <td>{p.likes ?? 0}</td>
 <td>{p.is_reported ? 'Yes' : 'No'}</td>
<td>
  {p.is_reported && (
    <button
      className="btn clear-report-btn"
      onClick={() => clearReportStatus(p.post_id)}
    >
      Clear Report
    </button>
  )}
  <button className="btn delete-btn" onClick={() => deletePost(p.post_id)}>
    Delete
  </button>
</td>
   </tr>
  ))}
     </tbody>
      </table>
    </div>
    {showModal && previewMedia && (
  <div className="media-modal" onClick={() => setShowModal(false)}>
    <div className="media-content" onClick={(e) => e.stopPropagation()}>
      <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
      {previewMedia.type === 'photo' ? (
        <img src={previewMedia.src} alt="Full preview" />
      ) : (
        <video controls autoPlay muted>
          <source src={previewMedia.src} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  </div>
)}

  </div>
)}
 {activeSection === 'comments' && (
  <div className="admin-section">
    <h2>Reported Comments</h2>

    <div className="admin-user-table-scroll">
      <table>
        <thead>
          <tr>
            <th>Comment</th>
            <th>Reported By</th>
            <th>Reason</th>
            <th>Post ID</th>
            <th>Comment Owner</th>
            <th>Date</th>
   <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {reportedComments.length === 0 ? (
            <tr>
              <td colSpan="6">No reported comments.</td>
            </tr>
          ) : (
            reportedComments.map((r) => (
              <tr key={r.id}>
                <td>{r.comment_text}</td>
                <td>{r.reported_by_email || r.reported_by}</td>
                <td>{r.reason}</td>
                <td>{r.post_id}</td>
                <td>{r.comment_owner_username || r.comment_owner_id}</td>
                <td>{new Date(r.timestamp).toLocaleDateString()}</td>
   <td>
  <button
    className="btn delete-btn"
    onClick={() => deleteReportedComment(r.comment_id)}
  >
    Delete
  </button>
</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
)}
{activeSection === 'reported-users' && (
  <div className="admin-section">
    <h2>Reported Users</h2>

    <p>Total Reports - {reportedUsers.length}</p>

    <div className="admin-user-table-scroll">
      <table>
        <thead>
          <tr>
            <th>Reported User</th>
            <th>Reported User Email</th>
            <th>Reported By</th>
            <th>Reporter Email</th>
            <th>Reason</th>
            <th>Details</th>
            <th>Total Reports</th>
            <th>Status</th>
            <th>Account Status</th>
            <th>Date</th>
          </tr>
        </thead>

        <tbody>
          {reportedUsers.length === 0 ? (
            <tr>
              <td colSpan="10">No reported users.</td>
            </tr>
          ) : (
            reportedUsers.map((report) => (
              <tr key={report.report_id}>
                <td>
                  {report.reported_username
                    ? `@${report.reported_username}`
                    : report.reported_name || report.reported_user_id}
                </td>

                <td>{report.reported_email || 'N/A'}</td>

                <td>
                  {report.reporter_username
                    ? `@${report.reporter_username}`
                    : report.reporter_name || report.reporter_user_id}
                </td>

                <td>{report.reporter_email || 'N/A'}</td>

                <td>
                  {report.reason
                    ? report.reason.replaceAll('_', ' ')
                    : 'N/A'}
                </td>

                <td>{report.details || 'No additional details'}</td>

                <td>{report.report_count ?? 1}</td>

                <td>{report.status || 'pending'}</td>

                <td>
                  {report.reported_is_blocked ? 'Banned' : 'Active'}
                </td>

                <td>
                  {report.created_at
                    ? new Date(report.created_at).toLocaleDateString()
                    : 'N/A'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
)}
</div>

    </div>
  );
}  

export default AdminDashboardPage;
